import React, {useRef} from 'react';
import {useParams, useSearchParams} from 'react-router';

import LoadingInTheMiddle from '../LoadingInTheMiddle';

import useGlobalSelectionLock from '../evidenceModal/useGlobalSelectionLock';
import useDocumentContent from '../evidenceModal/useDocumentContent';

import {useReportStore} from '../../features/products/useReportStore';
import useSourcePackageBuilderStore from '../../stores/useSourcePackageBuilderStore';

import DocumentContent from "../DocumentContent";
import DocumentContextMenus from "../DocumentContextMenus";
import useDocumentHtmlView from './hooks/useDocumentHtmlView';
import useDocumentHighlights from './hooks/useDocumentHighlights';
import useDocumentSelectionMenu from './hooks/useDocumentSelectionMenu';

// Stable empty array reference to avoid re-renders
const EMPTY_ARRAY = [];

// This component encapsulates all document-related logic and UI (content, selection, menus)
const DocumentView = () => {
    const [searchParams] = useSearchParams();
    const {reportId} = useParams();

    // Get documentId from zustand store instead of URL params
    const documentId = useSourcePackageBuilderStore(s => s.documentId);
    const isBuilderOpen = useSourcePackageBuilderStore(s => s.isOpen);

    // Selection lock while modal is open (only lock when viewing document outside builder)
    useGlobalSelectionLock(!!documentId && !isBuilderOpen);

    // Load document content
    const {isLoading, docData} = useDocumentContent(documentId);

    const currentDocKey = docData?.docId ?? documentId;

    // HTML mode
    const {viewMode, setViewMode, htmlSegments, loadingHtml} = useDocumentHtmlView(
        documentId,
        docData?.hasHTML
    );

    // Search result segment IDs (for marking matching segments with dotted border)
    const searchResultSegmentIdsFromStore = useReportStore((s) => s.searchResultSegmentIds);
    const searchResultSegmentIds = searchResultSegmentIdsFromStore?.length > 0
        ? searchResultSegmentIdsFromStore
        : EMPTY_ARRAY;

    // Highlight composition + cited state
    const {labeledHighlights, fragmentMarks, addOptimisticCited} = useDocumentHighlights({
        reportId,
        currentDocKey,
        docSegments: docData?.segments,
    });

    // Refs and context menu orchestration
    const contentRef = useRef(null);
    const {
        menuPosition,
        citeAnchorEl,
        setCiteAnchorEl,
        handleContextMenu,
        handleMouseDown,
        handleMenuClose,
        handleSaveForLater,
        handleCiteToFragment,
        handleDragStartSelection,
        onAddToSourceRoot,
        onAddToSourceFolder,
        onAddToSourceNewFolder,
        onAddToAnotherProduct,
    } = useDocumentSelectionMenu({
        reportId,
        currentDocKey,
        docData,
        contentRef,
        addOptimisticCited,
    });

    // Derive currently selected report fragment from URL (if any)
    const selectedElementName = searchParams.get('elementName');
    const selectedElementId = searchParams.get('elementId');
    const selectedFragmentUid = selectedElementName === 'report-fragment' ? selectedElementId : null;
    const selectedFragmentMark = React.useMemo(() => {
        if (!selectedFragmentUid) return null;
        return (fragmentMarks || []).find((f) => f.uid === selectedFragmentUid) || null;
    }, [fragmentMarks, selectedFragmentUid]);

    return (
        <>
            <div
                ref={contentRef}
                onContextMenu={handleContextMenu}
                onMouseDown={handleMouseDown}
                onDragStart={handleDragStartSelection}
                className={'evidence-allow-select'}
                style={{width: '100%'}}
            >
                {isLoading ? (
                    <LoadingInTheMiddle/>
                ) : docData ? (
                    <DocumentContent
                        props={docData}
                        savedHighlights={labeledHighlights}
                        searchResultSegmentIds={searchResultSegmentIds}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        htmlSegments={htmlSegments}
                        loadingHtml={loadingHtml}
                    />
                ) : null}
            </div>

            <DocumentContextMenus
                menuPosition={menuPosition}
                onClose={handleMenuClose}
                onSave={handleSaveForLater}
                citeAnchorEl={citeAnchorEl}
                setCiteAnchorEl={setCiteAnchorEl}
                fragmentMarks={fragmentMarks}
                selectedFragmentUid={selectedFragmentUid}
                selectedFragmentMark={selectedFragmentMark}
                onCiteToFragment={handleCiteToFragment}
                onAddToSourceRoot={onAddToSourceRoot}
                onAddToSourceFolder={onAddToSourceFolder}
                onAddToSourceNewFolder={onAddToSourceNewFolder}
                onAddToAnotherProduct={onAddToAnotherProduct}
            />
        </>
    );
};

export default DocumentView;

